/* eslint-disable @typescript-eslint/ban-ts-comment */
import { FC } from 'react';
import styled from 'styled-components';

import { MpParagraph } from 'shared/ui/MpParagraph';
import { MpPropertyTitle } from 'shared/ui/MpPropertyTitle';
import { MpText } from 'shared/ui/MpText';
import { MpUl } from 'shared/ui/MpUl';
import Eye from './icons/eye-img 1.svg?react';
import EyeOff from './icons/eyeoff-img 2.svg?react';

const ImgStyled = styled.img`
	margin-top: 8px;
	border-radius: 6px;
	@media (min-width: ${props => props.theme.breakpoints.xs}) {
		width: 328px;
	}

	@media (min-width: ${props => props.theme.breakpoints.sm}) {
		width: 496px;
	}

	@media (min-width: ${props => props.theme.breakpoints.md}) {
		width: 508px;
	}

	@media (min-width: ${props => props.theme.breakpoints.lg}) {
		width: 650px;
	}
`;

export const HrRegistryInsruction: FC = () => (
	<section>
		<div style={{ marginBottom: `24px` }}>
			<MpPropertyTitle>1. Заполнение полей поисковика</MpPropertyTitle>
			<MpParagraph>Чтобы получить информацию о кандидате введите в поисковик следующие параметры:</MpParagraph>
			<MpUl style={{ gap: `0` }}>
				<li>
					<MpText>Фамилия</MpText>
				</li>
				<li>
					<MpText>Имя</MpText>
				</li>
				<li>
					<MpText>Отчество</MpText>
				</li>
				<li>
					<MpText>Дата рождения</MpText>
				</li>
				<li>
					<MpText>Номер телефона</MpText>
				</li>
				<li>
					<MpText>ИНН</MpText>
				</li>
			</MpUl>
			<MpParagraph>
				Для поиска заполните минимум один из параметров. В поля «Фамилия», «Имя» и «Отчество» можно вводить
				информацию как с большой, так и с маленькой буквы. Чем больше данных будет введено, тем более
				релевантные результаты вы получите: учетные записи показываются от большего к меньшему количеству
				совпадений к поисковому запросу.
				<br />
				<br />
				Если в системе найдено более 20 совпадений (например, если искать только по фамилии), для сокращения
				выборки HR-реестр попросит вас ввести дополнительную информацию.
				<br />
				<br />В полученной выборке по каждой учетной записи полностью будут отображаться только те данные,
				которые были введены в поисковом запросе. Например, если запрос осуществляется только по ФИО, данные в
				полях «Дата рождения», «Номер телефона» и «ИНН» будут отображаться в зашифрованном виде.
			</MpParagraph>
			<ImgStyled
				src='https://cdn.dodostatic.net/static/marketplace/dodo-hr/instruction-1'
				alt='Employee search page screenshot'
			/>
		</div>
		<div style={{ marginBottom: `24px` }}>
			<MpPropertyTitle>2. Просмотр данных</MpPropertyTitle>
			<MpParagraph style={{ display: `flex`, alignItems: `center`, marginBottom: `0`, gap: `6px` }}>
				Для просмотра подробных данных о кандидате, нажмите на иконку <Eye />
			</MpParagraph>
			<MpParagraph>В раскрывающемся поле вы увидите информацию:</MpParagraph>
			<MpUl style={{ gap: `0` }}>
				<li>
					<MpText>С какой должности был уволен человек</MpText>
				</li>
				<li>
					<MpText>Дата приема на работу</MpText>
				</li>
				<li>
					<MpText>Дата увольнения</MpText>
				</li>
				<li>
					<MpText>Причина увольнения</MpText>
				</li>
				<li>
					<MpText>Номер пиццерии</MpText>
				</li>
				<li>
					<MpText>Общий номер телефона пиццерии</MpText>
				</li>
				<li>
					<MpText>Имя управляющего (для уточнения обратной связи по кандидату)</MpText>
				</li>
			</MpUl>
			<MpParagraph style={{ display: `flex`, alignItems: `center`, gap: `6px` }}>
				Чтобы свернуть данные, нажмите на иконку <EyeOff />
			</MpParagraph>
			<ImgStyled
				src='https://cdn.dodostatic.net/static/marketplace/dodo-hr/instruction-2'
				alt='Employee search result page screenshot'
			/>
		</div>
		<div style={{ marginBottom: `24px` }}>
			<MpPropertyTitle>3. Новый поиск</MpPropertyTitle>
			<MpParagraph>Для нового поиска, введите данные следующего кандидата или нажмите на «Сброс».</MpParagraph>
		</div>
		<div>
			<MpPropertyTitle>4. Поддержка</MpPropertyTitle>
			<MpParagraph>
				Если у вас возникли сложности при работе с HR-реестром, обратитесь в техническую поддержку через форму
				обращений.
			</MpParagraph>
		</div>
	</section>
);
// eslint-disable-next-line @typescript-eslint/naming-convention
export const HrRegistryInsruction_eng: FC = () => (
	<section>
		<div style={{ marginBottom: `24px` }}>
			<MpPropertyTitle>1. Filling in the Search Fields</MpPropertyTitle>
			<MpParagraph>
				To obtain information about a candidate, enter the following parameters in the search:
			</MpParagraph>
			<MpUl style={{ gap: `0` }}>
				<li>
					<MpText>Last Name</MpText>
				</li>
				<li>
					<MpText>First Name</MpText>
				</li>
				<li>
					<MpText>Middle Name</MpText>
				</li>
				<li>
					<MpText>Date of Birth</MpText>
				</li>
				<li>
					<MpText>Phone Number</MpText>
				</li>
				<li>
					<MpText>Tax ID (INN)</MpText>
				</li>
			</MpUl>
			<MpParagraph>
				To search, fill in at least one of the parameters. In the &quot;Last Name&quot;, &quot;First Name&quot;,
				and &quot;Middle Name&quot; fields, you can enter information in both uppercase and lowercase. The more
				data is entered, the more relevant results you will get: accounts are displayed from more to fewer
				matches to the search query.
				<br />
				<br />
				If more than 20 matches are found in the system (for example, if searching only by last name), to reduce
				the selection, the HR Registry will ask you to enter additional information.
				<br />
				<br />
				In the obtained selection, for each account, only the data that was entered in the search query will be
				fully displayed. For example, if the query is made only by full name, data in the &quot;Date of
				Birth&quot;, &quot;Phone Number&quot;, and &quot;Tax ID&quot; fields will be displayed in encrypted
				form.
			</MpParagraph>
			<ImgStyled
				src='https://cdn.dodostatic.net/static/marketplace/dodo-hr/instruction-1'
				alt='Employee search page screenshot'
			/>
		</div>
		<div style={{ marginBottom: `24px` }}>
			<MpPropertyTitle>2. Viewing Data</MpPropertyTitle>
			<MpParagraph style={{ display: `flex`, alignItems: `center`, marginBottom: `0`, gap: `6px` }}>
				To view detailed data about the candidate, click on the <Eye /> icon.
			</MpParagraph>
			<MpParagraph>You will see the information in the expanded field:</MpParagraph>
			<MpUl style={{ gap: `0` }}>
				<li>
					<MpText>From which position the person was dismissed</MpText>
				</li>
				<li>
					<MpText>Date of employment</MpText>
				</li>
				<li>
					<MpText>Date of dismissal</MpText>
				</li>
				<li>
					<MpText>Reason for dismissal</MpText>
				</li>
				<li>
					<MpText>Pizzeria number</MpText>
				</li>
				<li>
					<MpText>General phone number of the pizzeria</MpText>
				</li>
				<li>
					<MpText>Manager&apos;s name (for feedback clarification about the candidate)</MpText>
				</li>
			</MpUl>
			<MpParagraph style={{ display: `flex`, alignItems: `center`, gap: `6px` }}>
				To collapse the data, click on the <EyeOff /> icon.
			</MpParagraph>
			<ImgStyled
				src='https://cdn.dodostatic.net/static/marketplace/dodo-hr/instruction-2'
				alt='Employee search result page screenshot'
			/>
		</div>
		<div style={{ marginBottom: `24px` }}>
			<MpPropertyTitle>3. New Search</MpPropertyTitle>
			<MpParagraph>
				For a new search, enter the details of the next candidate or click &quot;Reset&quot;.
			</MpParagraph>
		</div>
		<div>
			<MpPropertyTitle>4. Support</MpPropertyTitle>
			<MpParagraph>
				If you encounter difficulties working with the HR Registry, contact technical support through the
				support form.
			</MpParagraph>
		</div>
	</section>
);
